import { useState } from 'react';
import {
  AlertTriangle, BatteryCharging, Baby, ChevronRight, Clock, Droplets, ExternalLink,
  FileSignature, Flame, HelpCircle, Lock, Play, Power, RotateCcw, Settings2, ShieldAlert, Snowflake,
  Square, Sun, ThermometerSun, Zap, Gauge,
} from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import BatteryControlPanel from '@/shared/components/BatteryControlPanel';
import ProductionPvControl from '@/features/pilotage/components/ProductionPvControl';
import PacReadout from '@/features/assets/components/PacReadout';
import ConnexionBadge from '@/shared/components/ConnexionBadge';
import { MANDAT_MANQUANT } from '@/shared/constants/statuses';
import {
  ageJoignabilite, dernierPointPvTexte, donneesPerimees, etatJoignabilite,
  etatOnduleurTexte, horodatageTexte, mesureTexte, productionCouranteTexte, vuA,
} from '@/shared/utils/liveness';

export const TYPE_ICON = {
  PAC: Flame, PV: Sun, Ballon: Droplets, ECS: Droplets, Batterie: BatteryCharging,
};
export const TYPE_COLOR = {
  PAC: 'text-orange-500 bg-orange-50', PV: 'text-amber-500 bg-amber-50',
  Ballon: 'text-cyan-500 bg-cyan-50', ECS: 'text-cyan-500 bg-cyan-50',
  Batterie: 'text-violet-500 bg-violet-50',
};
export const TYPE_LABEL = {
  PAC: 'PAC', PV: 'PV', Ballon: 'Ballon ECS', ECS: 'Ballon ECS', Batterie: 'Batterie',
};

const ACTION_ICON = {
  on: Power, off: Power,
  mode_chauffe: ThermometerSun, mode_froid: Snowflake,
  set_consigne: Settings2, set_min: Settings2, set_max: Settings2,
  antigel_on: ShieldAlert, antigel_off: ShieldAlert,
  manuel: Settings2, auto: RotateCcw,
  verrou_on: Baby, verrou_off: Baby,
  forcage: Zap,
  // Batterie : `auto` reprend l'icône « retour », `power_zero` celle de la coupure.
  power_zero: Square,
  charge_start: Play, decharge_start: Play,
  restart: RotateCcw,
  // PV : « limiter » emprunte l icone de la jauge, « production libre » celle du
  // retour a la normale, comme `auto` sur la batterie. Les deux cles
  // `zero_injection_*` ont disparu avec le lot de pilotage PV du 14/09/2026 :
  // les garder ici aurait laisse croire que les actions existent encore.
  limiter_production: Gauge, production_libre: RotateCcw,
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/** Métrique compacte du bandeau de monitoring. */
function Metric({ label, value, accent, tid }) {
  return (
    <div data-testid={tid}>
      <p className="text-neutral-400 text-[10px]">{label}</p>
      <p className={`font-semibold ${accent ? 'text-cyan-700' : 'text-neutral-900'}`}>{value}</p>
    </div>
  );
}

/** Bandeau de télémétrie — mêmes valeurs que la page Monitoring (même enrichissement API). */
function MonitoringStrip({ asset }) {
  const m = asset.monitoring;
  if (!m || !Object.keys(m).length) return null;
  // Sur une machine qui ne répond plus, ces chiffres sont un souvenir. On les grise et on
  // les date : un chiffre périmé présenté comme actuel est ce qui a coûté deux heures.
  const perime = donneesPerimees(asset);
  const releve = horodatageTexte(vuA(asset) || asset.dernier_contact);
  return (
    <div className="border-t border-neutral-100 bg-gradient-to-r from-neutral-50/80 to-white">
      {perime && (
        <p className="px-4 pt-2 text-[10px] text-amber-700" data-testid={`stale-${asset.id}`}>
          Derniers chiffres connus{releve ? `, relevés le ${releve}` : ''}. Ce n'est pas
          l'état actuel.
        </p>
      )}
      <div
        className={`px-4 py-2.5 grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px] ${perime ? 'opacity-60' : ''}`}
        data-testid={`monitoring-${asset.id}`}
      >
        {asset.type === 'PAC' && (<>
          <Metric label="État" value={m.etat || '—'} accent={m.etat === 'ON'} />
          <Metric label="Mode" value={m.mode || '—'} />
          {/* ⚠️ CES TROIS CHAMPS LISAIENT DES NOMS QU'AUCUN ÉMETTEUR N'ÉCRIT POUR UNE PAC
              RÉELLE, relevé de David en vidéo le 24/09 (« pas de data qui monte ») :
              la veille Tuya traduit `temp_current` en `temperature_mesuree`
              (`core/releve_pac.traduire`), jamais en `temperature_c`, qui est un champ
              BATTERIE. La ligne affichait donc un tiret sur un thermostat qui mesurait
              22,5 °C, pendant que le Monitoring, qui lit le bon nom, affichait la valeur.
              Et `?? 0` peignait « 0 kW » sur un matériel qui ne mesure pas sa
              consommation : « inconnu » dit la vérité. Mêmes noms que
              `AssetMonitoringCard`, pour que les deux écrans ne divergent plus. */}
          <Metric label="Consommation" value={mesureTexte(m.consommation_kw ?? m.puissance_consommee_kw, 'kW')} />
          <Metric label="Température" value={mesureTexte(m.temperature_mesuree ?? m.temperature_c, '°C')} />
          <Metric label="Consigne" value={mesureTexte(m.temperature_consigne, '°C')} />
          <Metric label="Dégivrage" value={m.en_degivrage == null ? 'inconnu' : (m.en_degivrage ? 'Oui' : 'Non')} accent={m.en_degivrage} />
        </>)}
        {/* Memes deux corrections que sur la carte de supervision et la fiche client :
            `?? 0` peignait une absence de mesure en « 0 kW », et « Rendement » affichait
            `0 %` pour une grandeur que l'API ne rend jamais. Voir
            `shared/utils/liveness`, section « CENTRALES PHOTOVOLTAIQUES ». */}
        {asset.type === 'PV' && (<>
          <Metric label="Production" value={productionCouranteTexte(m)} accent />
          <Metric label="Aujourd'hui" value={mesureTexte(m.energie_jour_kwh, 'kWh')} />
          {/* LA PUISSANCE INSTALLEE VIENT DE L ACTIF, PAS DES MESURES, et c est la valeur
          que HOYMILES declare — jamais `puissance_kw`, saisie dans Flexcore. Ecart
          mesure sur la centrale du showroom le 14/09 : 0,5 kWc chez Hoymiles contre
          1,0 kW dans la fiche. Afficher la seconde donnerait une puissance fausse du
          DOUBLE, sous un libelle credible.

          ⚠️ CE CHAMP LISAIT `m.puissance_crete_kwc` JUSQU AU 14/09, et affichait donc
          un tiret perpetuel : aucun emetteur du backend n a jamais ecrit ce nom. Un
          couple lecteur/emetteur avait diverge en silence. Meme arbitrage et meme
          source que `CentralePvPanel`, qui porte le raisonnement complet. */}
          <Metric label="Puissance crête" value={mesureTexte(asset.capacite_kwc, 'kWc')} />
          <Metric label="Onduleur" value={etatOnduleurTexte(m)} />
          {!m.puissance_courante_prouvee && dernierPointPvTexte(m) && (
            <Metric label="Dernier point" value={dernierPointPvTexte(m)} />
          )}
        </>)}
        {(asset.type === 'Ballon' || asset.type === 'ECS') && (<>
          <Metric label="État" value={m.etat || '—'} accent={m.etat === 'ON'} />
          <Metric label="Température eau" value={`${m.temperature_eau_c ?? '—'} °C`} />
          <Metric label="Consigne" value={`${m.temperature_consigne_c ?? '—'} °C`} />
          <Metric label="Connexion" value={asset.statut_connexion} accent={asset.statut_connexion === 'connecte'} />
        </>)}
        {asset.type === 'Batterie' && (<>
          <Metric label="État" value={m.etat && m.etat !== 'inconnu' ? m.etat : 'inconnu'} accent={m.etat === 'charge'} />
          <Metric label="SOC" value={mesureTexte(m.soc_pct, '%')} accent />
          <Metric label="Puissance" value={mesureTexte(m.puissance_kw, 'kW')} />
          <Metric label="Tension" value={mesureTexte(m.tension_v, 'V')} />
          <Metric label="Santé" value={mesureTexte(m.sante_pct, '%')} accent />
        </>)}
      </div>
    </div>
  );
}

/** Un bouton d'action, ou un champ + OK pour les actions à consigne. */
function ActionControl({ asset, code, def, pending, setPending, onAction }) {
  const Icon = ACTION_ICON[code] || ChevronRight;
  const blocked = !!def.unavailable;
  const simulated = def.integration === 'simulated';
  // Une action simulée doit se voir AVANT le clic : l'admin sait qu'il ne pilote rien.
  const title = def.unavailable
    || (simulated ? 'Aucune intégration branchée — le geste est tracé, sans effet matériel' : def.note);

  if (def.family === 'value') {
    // Le champ démarre sur `default_value` quand le catalogue en propose une : une
    // consigne de charge a une valeur usuelle, l'admin doit pouvoir valider sans
    // ressaisir. Tant qu'il n'a rien tapé, `pending` est undefined et c'est le défaut
    // qui part — la valeur affichée est donc toujours celle qui sera envoyée.
    const current = pending ?? (def.default_value != null ? String(def.default_value) : '');
    return (
      <div className="flex items-center gap-1 bg-white rounded-md border border-neutral-200/80 px-2 py-1">
        <Icon className="w-3.5 h-3.5 text-neutral-500" />
        <span className="text-[11px] text-neutral-600" title={title}>{def.label}</span>
        {simulated && <SimulatedDot />}
        <Input
          type="number"
          // w-16 tronquait « 1000 » en « 100 » une fois la molette du champ nombre
          // affichée : une consigne à moitié lisible est pire que pas de consigne.
          className="h-7 w-[76px] text-[11px]"
          placeholder={def.value_unit || ''}
          min={def.value_min}
          max={def.value_max}
          value={current}
          disabled={blocked}
          onChange={e => setPending(e.target.value)}
          data-testid={`value-input-${asset.id}-${code}`}
        />
        {def.value_unit && (
          <span className="text-[10px] text-neutral-400">{def.value_unit}</span>
        )}
        <Button
          size="sm" variant="outline" className="h-7 px-2 text-[10px]"
          disabled={blocked || current === ''}
          onClick={() => onAction(code, parseFloat(current))}
          data-testid={`apply-${asset.id}-${code}`}
        >
          OK
        </Button>
      </div>
    );
  }

  // Un seul bouton plein par ligne : celui qui remet l'actif en marche. Sur une
  // batterie c'est `auto` (retour à la régulation constructeur), ailleurs c'est `on` —
  // une PAC a les deux, et deux boutons pleins côte à côte ne hiérarchisent plus rien.
  const isResume = asset.type === 'Batterie' ? code === 'auto' : code === 'on';
  // Rouge pour ce qui arrête un échange d'énergie : à ne pas cliquer par inadvertance.
  const isHalt = code === 'off' || code === 'power_zero';

  return (
    <Button
      variant={isResume ? 'default' : 'outline'}
      size="sm"
      title={title}
      disabled={blocked}
      className={`h-7 text-[11px] ${isHalt ? 'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700' : ''}`}
      onClick={() => onAction(code, null)}
      data-testid={`action-${asset.id}-${code}`}
    >
      <Icon className="w-3.5 h-3.5 mr-1" /> {def.label}
      {simulated && <SimulatedDot />}
    </Button>
  );
}

function SimulatedDot() {
  return (
    <span
      className="ml-1 px-1 rounded bg-neutral-200 text-neutral-600 text-[9px] font-medium uppercase tracking-wide"
      data-testid="simulated-badge"
    >
      simulé
    </span>
  );
}

/**
 * Une ligne d'actif de l'écran de pilotage : sélection, identité, télémétrie, actions.
 *
 * Un actif verrouillé par un agrégateur n'est ni sélectionnable ni pilotable — la case
 * est désactivée, le panneau d'actions ne s'ouvre pas. On ne masque pas la ligne pour
 * autant : l'admin doit voir que l'actif existe et pourquoi il ne peut pas y toucher.
 *
 * DEPUIS LE LOT U, UN ACTIF SANS MANDAT SIGNÉ SUIT EXACTEMENT LE MÊME TRAITEMENT.
 *
 * Il en était purement absent, ce qui faisait passer un enregistrement réussi pour un
 * échec. Il est maintenant listé, marqué, et son bandeau nomme le geste qui le
 * débloque — signer le mandat.
 *
 * ⚠️ CE QUI EST FAIT ICI N'EST PAS LE REFUS, C'EST SON AFFICHAGE. Le refus vit côté
 * serveur (`core/mandat`, 409 sur les routes d'exécution) et tient même contre un appel
 * direct. Retirer ce bandeau ne rendrait pas l'actif pilotable pour autant.
 */
export default function AssetPilotageRow({
  asset, actions, selected, onToggle, onAction, onOpenClient,
}) {
  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState({});
  const Icon = TYPE_ICON[asset.type] || HelpCircle;
  const locked = asset.aggregateur_lock;
  // `pilotable` est le verdict du SERVEUR, pas une relecture des statuts de mandat :
  // deux lectures de la même règle finissent toujours par diverger, et celle qui
  // compte est celle qui refuse. `!== false` et non `=== false` par prudence : un
  // actif rendu par une version d'API qui n'enverrait pas le champ reste pilotable
  // comme avant, au lieu d'être bloqué en silence.
  // `sans_mandat` et non `pilotable === false` — arbitrage du 31/08. Les deux faits
  // ont été séparés : un actif sans mandat SE PILOTE, il se signale. `pilotable` ne
  // parle plus que du verrou agrégateur.
  const sansMandat = asset.sans_mandat === true;
  // Deux raisons de ne pas agir, un seul comportement d'écran. Le verrou agrégateur est
  // transitoire, le mandat manquant appelle une signature — d'où deux bandeaux.
  const inerte = locked || sansMandat;
  const deviceLabel = asset.type === 'PAC' ? 'Device ID' : asset.type === 'PV' ? 'ID Hoymiles' : 'ID';
  // Joignabilité déduite AVANT le clic : c'est la question à laquelle il faut répondre
  // avant d'envoyer un ordre, pas après. Elle vit dans le bloc monitoring pour une
  // batterie, dans l'ancien champ à deux valeurs pour les autres types.
  const etatConnexion = etatJoignabilite(asset);
  const ageConnexion = ageJoignabilite(asset);

  return (
    <div
      className={`rounded-lg border overflow-hidden ${inerte ? 'border-amber-200 bg-amber-50/30' : 'border-neutral-200/80 bg-white'}`}
      data-testid={`asset-row-${asset.id}`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          disabled={inerte}
          data-testid={`select-${asset.id}`}
        />
        <div className={`w-9 h-9 rounded-md flex items-center justify-center ${TYPE_COLOR[asset.type] || 'bg-neutral-50'}`}>
          <Icon className="w-4 h-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-[13px] font-medium text-neutral-900 truncate">{asset.client_nom}</p>
            <span className="text-[10px] text-neutral-400">·</span>
            <p className="text-[11px] text-neutral-500 truncate">
              {TYPE_LABEL[asset.type] || asset.type} — {asset.marque} {asset.modele}
            </p>
          </div>
          {(asset.thermostat_reference || asset.identifiant_equipement) && (
            <p className="text-[10px] text-neutral-400 font-mono truncate" data-testid={`asset-device-${asset.id}`}>
              {asset.thermostat_reference && (
                <span className="mr-2">Thermostat : <span className="text-neutral-500">{asset.thermostat_reference}</span></span>
              )}
              <span>{deviceLabel} : {asset.thermostat_serial || asset.identifiant_equipement}</span>
            </p>
          )}
          <div className="flex items-center gap-3 text-[10px] text-neutral-400">
            <ConnexionBadge
              etat={etatConnexion}
              ageS={ageConnexion}
              genre={asset.type === 'Batterie' ? 'f' : 'm'}
              testId={`conn-${asset.id}`}
            />
            {asset.client_ville && <span>{asset.client_ville}</span>}
            {asset.dernier_contact && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Dernier contact : {fmtDate(asset.dernier_contact)}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={onOpenClient}
          className="text-[11px] text-neutral-500 hover:text-neutral-900 flex items-center gap-0.5"
          title="Ouvrir la fiche client"
          data-testid={`open-client-${asset.id}`}
        >
          <ExternalLink className="w-3 h-3" />
        </button>

        {sansMandat ? (
          <Badge
            variant="outline"
            className={`text-[10px] whitespace-nowrap ${MANDAT_MANQUANT.cls}`}
            title={MANDAT_MANQUANT.aide}
            data-testid={`mandat-badge-${asset.id}`}
          >
            <FileSignature className="w-3 h-3 mr-1" /> {MANDAT_MANQUANT.label}
          </Badge>
        ) : locked ? (
          <Badge
            variant="outline"
            className="bg-amber-100 text-amber-800 border-amber-200 text-[10px] whitespace-nowrap"
            data-testid={`lock-badge-${asset.id}`}
          >
            <Lock className="w-3 h-3 mr-1" /> Agrégateur en cours
          </Badge>
        ) : (
          <Button
            variant="ghost" size="sm" className="text-[11px] h-7"
            onClick={() => setExpanded(v => !v)}
            data-testid={`expand-${asset.id}`}
          >
            {expanded ? 'Masquer' : 'Piloter'}
            <ChevronRight className={`w-3 h-3 ml-0.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </Button>
        )}
      </div>

      {/* LE BANDEAU DIT LE GESTE, PAS SEULEMENT L'EMPÊCHEMENT.
          « Non pilotable » laisse chercher ; « signez le mandat » se répare. Le texte
          vient du serveur, pour que l'API et l'écran ne puissent pas diverger. */}
      {sansMandat && (
        <div
          className="px-4 py-2 border-t border-amber-100 bg-amber-50/70 text-[11px] text-amber-800 flex items-center gap-2"
          data-testid={`mandat-banniere-${asset.id}`}
        >
          <FileSignature className="w-3 h-3 flex-shrink-0" />
          {asset.avertissement_mandat || MANDAT_MANQUANT.aide}
        </div>
      )}

      {locked && !sansMandat && (
        <div className="px-4 py-2 border-t border-amber-100 bg-amber-50/70 text-[11px] text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" />
          Activation agrégateur en cours — pilotage temporairement indisponible
        </div>
      )}

      <MonitoringStrip asset={asset} />

      {expanded && !inerte && (
        <div
          className="border-t border-neutral-100 px-4 py-3 bg-neutral-50/40 flex flex-wrap gap-2"
          data-testid={`actions-${asset.id}`}
        >
          {/* La batterie a son propre panneau : une seule consigne de puissance, partagée
              par charge et décharge. Le rendu générique donnerait un champ par action, ce
              qui laisse croire à deux réglages indépendants. */}
          {asset.type === 'Batterie' ? (
            <BatteryControlPanel
              actions={actions} onAction={onAction}
              etatConnexion={etatConnexion} ageS={ageConnexion}
              mqttStatus={asset.monitoring?.mqtt_status || asset.statut_connexion}
              consigne={asset.monitoring?.consigne_w != null ? asset.monitoring : null} />
          ) : (
            <>
              {/* Le relevé complet de la PAC au-dessus de ses actions, comme le Monitoring
                  (01/10) : piloter sans voir l'état obtenu, c'est piloter à l'aveugle. */}
              {asset.type === 'PAC' && (
                <div className="w-full mb-1"><PacReadout asset={asset} /></div>
              )}
              {Object.entries(actions).map(([code, def]) => (
                <ActionControl
                  key={code}
                  asset={asset} code={code} def={def}
                  pending={pending[code]}
                  setPending={v => setPending(p => ({ ...p, [code]: v }))}
                  onAction={onAction}
                />
              ))}
              {/* Le vrai arrêt d'une centrale, à côté du bridage : voir
                  `ProductionPvControl`. Il vise les onduleurs, pas l'actif, d'où un
                  composant hors du catalogue d'actions. */}
              {asset.type === 'PV' && <ProductionPvControl asset={asset} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}
